// =============================================================================
// Admin Analytics — filter builders for the `adminAnalytics` GraphQL resource
// =============================================================================
// Page-level filters (the sticky filter bar) become `AnalyticsFiltersInput`;
// everything a single cell adds on top — its time override, category, report
// type override, and a drilldown's date range + facet selections — becomes
// `AnalyticsCellFiltersInput`. A value set on the cell replaces the page-level
// value for that cell only; the backend does the merge.
// =============================================================================
import { CalendarDateRange, ComparisonLabel } from "@fronterahealth/frontera-ui-components";

import {
  AnalyticsCellFiltersInput,
  AnalyticsComparisonInput,
  AnalyticsComparisonMode,
  AnalyticsDateRangeInput,
  AnalyticsFacetSearchInput,
  AnalyticsFiltersInput,
  AnalyticsPageInput,
  AnalyticsQaCategory,
  AnalyticsQaFlagStatus,
  ReportAnalyticsStatus,
  ReportAnalyticsTemplateType,
} from "@api/graphql/types-and-hooks";
import { FacetOption, FilterValues } from "@pages/AdminAnalytics/components/DrilldownFilters";
import {
  CATEGORY_FROM_API,
  CATEGORY_TO_API,
  QA_FLAG_STATUS_LABELS,
  STATUS_FROM_API,
  TEMPLATE_TYPE_FROM_API,
  TEMPLATE_TYPE_TO_API,
  toEnumValues,
} from "@pages/AdminAnalytics/data/enumMaps";
import {
  QA_CATEGORY_LABELS,
  QaCategory,
  REPORT_STATUS_LABELS,
  TEMPLATE_TYPE_LABELS,
  TemplateType,
} from "@pages/AdminAnalytics/data/types";
import { formatTemplateName, toComparisonContract, toRangeContract } from "@pages/AdminAnalytics/helpers";

const sorted = <T extends string>(values: readonly T[]): T[] => [...values].sort((a, b) => a.localeCompare(b));

const DERIVED_COMPARISON_MODES: Partial<Record<ComparisonLabel, AnalyticsComparisonMode>> = {
  "Previous Period": AnalyticsComparisonMode.PreviousPeriod,
  "Same Period Last Year": AnalyticsComparisonMode.SamePeriodLastYear,
};

/**
 * The picker's comparison choice as the server takes it: the mode, plus dates
 * only for a custom comparison (the server derives the others from the range).
 * `undefined` = no comparison — "No Comparison", or a custom comparison whose
 * dates aren't both picked yet (the server rejects CUSTOM without dates).
 */
export const toComparisonInput = (range: CalendarDateRange | undefined): AnalyticsComparisonInput | undefined => {
  const custom = toComparisonContract(range);
  // The picker always reports its label; dates alone can only mean a custom comparison.
  const label = range?.selectedComparisonLabel ?? (custom ? "Custom Comparison" : undefined);
  if (label === "Custom Comparison") {
    return custom ? { mode: AnalyticsComparisonMode.Custom, fromDate: custom.start, toDate: custom.end } : undefined;
  }
  const mode = label ? DERIVED_COMPARISON_MODES[label] : undefined;
  return mode ? { mode } : undefined;
};

/** A page-level range: the days, and how to compare them. */
export const toDateRangeInput = (range: CalendarDateRange | undefined): AnalyticsDateRangeInput => {
  const current = toRangeContract(range);
  const comparison = toComparisonInput(range);
  return { fromDate: current.start, toDate: current.end, ...(comparison ? { comparison } : {}) };
};

/**
 * A cell's range (a time override or a drilldown's dates) sends no comparison
 * of its own, so it inherits the page-level mode, which the server re-derives
 * for the cell's range. (The override picker offers no comparison choice.)
 */
const toCellDateRangeInput = (range: CalendarDateRange): AnalyticsDateRangeInput => {
  const current = toRangeContract(range);
  return { fromDate: current.start, toDate: current.end };
};

/** Page-level filters. `templateTypes` must already be resolved against the org config. */
export const buildPageFilters = (
  range: CalendarDateRange | undefined,
  templateTypes: TemplateType[],
  templateNames: string[],
): AnalyticsFiltersInput => ({
  dateRange: toDateRangeInput(range),
  ...(templateTypes.length
    ? { reportTypes: sorted(templateTypes).map((t) => TEMPLATE_TYPE_TO_API[t as TemplateType]) }
    : {}),
  ...(templateNames.length ? { templateGroups: sorted(templateNames) } : {}),
});

// ----- Cell filters -----------------------------------------------------------

/** What a widget layers on top of the page-level filters. Empty = "use the page filters". */
export interface CellFilters {
  /** The widget's time override (or a drilldown's date range). */
  dateRange?: CalendarDateRange;
  /** A widget-local report-type override (the writing-time card). */
  reportTypes?: TemplateType[];
  categories?: QaCategory[];
  /** Fixed per drilldown (e.g. the Open QA Flags dialog only ever shows open flags). */
  qaStatus?: AnalyticsQaFlagStatus;
}

/** `undefined` when the cell adds nothing — i.e. it can read from the dashboard query. */
export const toCellFiltersInput = (cell: CellFilters): AnalyticsCellFiltersInput | undefined => {
  const input: AnalyticsCellFiltersInput = {
    ...(cell.dateRange ? { dateRange: toCellDateRangeInput(cell.dateRange) } : {}),
    ...(cell.reportTypes?.length
      ? { reportTypes: sorted(cell.reportTypes).map((t) => TEMPLATE_TYPE_TO_API[t as TemplateType]) }
      : {}),
    ...(cell.categories?.length ? { categories: sorted(cell.categories.map((c) => CATEGORY_TO_API[c])) } : {}),
    ...(cell.qaStatus ? { qaStatus: cell.qaStatus } : {}),
  };
  return Object.keys(input).length ? input : undefined;
};

/**
 * Facet keys the backend reports, mapped to the cell filter each one feeds.
 * Facet `value`s are exactly what the matching filter takes. Values are sorted
 * so the same selection made in a different order is the same query (and
 * cache entry).
 */
export const selectionsToCellFilters = (selections: FilterValues): AnalyticsCellFiltersInput => {
  const qaStatus = toEnumValues(selections.qaStatus, AnalyticsQaFlagStatus);
  const filters: AnalyticsCellFiltersInput = {
    learners: selections.learner && sorted(selections.learner),
    templateGroups: selections.templateGroup && sorted(selections.templateGroup),
    providerIds: selections.provider && sorted(selections.provider),
    reportTypes: sorted(toEnumValues(selections.reportType, ReportAnalyticsTemplateType)),
    statuses: sorted(toEnumValues(selections.status, ReportAnalyticsStatus)),
    categories: sorted(toEnumValues(selections.category, AnalyticsQaCategory)),
    // Selecting both Open and Resolved is the same as no status filter.
    qaStatus: qaStatus.length === 1 ? qaStatus[0] : undefined,
  };
  return Object.fromEntries(
    Object.entries(filters).filter(([, v]) => (Array.isArray(v) ? v.length > 0 : v !== undefined)),
  ) as AnalyticsCellFiltersInput;
};

/** Per facet key, the values a drilldown may offer and send; a missing/empty entry means "any". */
export type FacetScope = Partial<Record<string, readonly string[]>>;

/** Drop selected values outside `scope` — a key left with nothing falls back to inheriting. */
const withinScope = (selections: FilterValues, scope: FacetScope): FilterValues =>
  Object.fromEntries(
    Object.entries(selections)
      .map(([key, values]) => {
        const allowed = scope[key];
        return [key, allowed?.length ? values.filter((v) => allowed.includes(v)) : values] as const;
      })
      .filter(([, values]) => values.length > 0),
  );

/**
 * A drilldown's filters: the widget's own, then the dialog's date range, then
 * its facet selections — clamped to `scope`, so a selection can only narrow
 * the page-level report types / templates, never replace them with others.
 */
export const buildDrilldownFilters = (
  base: CellFilters,
  dialogDate: CalendarDateRange | undefined,
  selections: FilterValues,
  scope: FacetScope = {},
): AnalyticsCellFiltersInput => ({
  ...toCellFiltersInput(base),
  ...(dialogDate ? { dateRange: toCellDateRangeInput(dialogDate) } : {}),
  ...selectionsToCellFilters(withinScope(selections, scope)),
});

// ----- Pagination -------------------------------------------------------------

/** 1-based page + page size, as the Mosaic table reports them. */
export interface PageState {
  page: number;
  pageSize: number;
}

export const toPageInput = ({ page, pageSize }: PageState): AnalyticsPageInput => ({
  offset: (page - 1) * pageSize,
  limit: pageSize,
});

export const firstPage = (limit: number): AnalyticsPageInput => ({ offset: 0, limit });

// ----- Facet options ----------------------------------------------------------

export interface FacetPayload {
  key: string;
  /** More values matched than the server returned; search to narrow. */
  truncated?: boolean;
  values: ReadonlyArray<{ value: string; label: string; count: number }>;
}

/** Values the server returns per facet key (plus any selected value the limit left out). */
export const FACET_LIMIT = 50;

/**
 * Facets that can have far more values than `FACET_LIMIT` on a large org, so
 * their value lists search on the server. The rest are short and filter in the
 * browser — including template groups: an org has a handful, and the server
 * would match the raw name ("ABC_STANDARD") where the list shows "Abc Standard".
 */
export const SEARCHABLE_FACET_KEYS: readonly string[] = ["learner", "provider"];

/** The open value list's server-side search: facet key → query. */
export type FacetSearch = Record<string, string>;

/** The server-side facet search, sorted by key so the same search is the same query. */
export const toFacetSearchInput = (search: FacetSearch): AnalyticsFacetSearchInput[] | undefined => {
  const input = Object.entries(search)
    .map(([key, query]) => ({ key, query: query.trim() }))
    .filter(({ key, query }) => query && SEARCHABLE_FACET_KEYS.includes(key))
    .sort((a, b) => a.key.localeCompare(b.key));
  return input.length ? input : undefined;
};

/** Searchable facet keys the server cut short at `FACET_LIMIT`. */
export const truncatedFacetKeys = (facets: readonly FacetPayload[] | null | undefined): string[] =>
  (facets ?? []).filter((f) => f.truncated && SEARCHABLE_FACET_KEYS.includes(f.key)).map((f) => f.key);

/** Enum facets arrive labeled with their enum name; show the UI's own labels instead. */
const FACET_LABELS: Record<string, (value: string) => string | undefined> = {
  reportType: (v) => TEMPLATE_TYPE_LABELS[TEMPLATE_TYPE_FROM_API[v as ReportAnalyticsTemplateType]],
  status: (v) => REPORT_STATUS_LABELS[STATUS_FROM_API[v as ReportAnalyticsStatus]],
  category: (v) => QA_CATEGORY_LABELS[CATEGORY_FROM_API[v as AnalyticsQaCategory]],
  qaStatus: (v) => QA_FLAG_STATUS_LABELS[v as AnalyticsQaFlagStatus],
  templateGroup: (v) => formatTemplateName(v),
};

export const facetLabel = (key: string, value: string, serverLabel = value): string =>
  FACET_LABELS[key]?.(value) ?? serverLabel;

/**
 * Facet payload → per-key options. `allowed` narrows a key to values inside the
 * scope above the dialog: a facet is counted without its own key's filter, so
 * e.g. the report-type facet can list types the page-level filter excludes —
 * and selecting one would replace that page-level filter for the dialog.
 */
export const toFacetOptions = (
  facets: readonly FacetPayload[] | null | undefined,
  allowed: FacetScope = {},
): Record<string, FacetOption[]> =>
  Object.fromEntries(
    (facets ?? []).map((facet) => {
      const keep = allowed[facet.key];
      return [
        facet.key,
        facet.values
          .filter((v) => !keep?.length || keep.includes(v.value))
          .map((v) => ({ value: v.value, label: facetLabel(facet.key, v.value, v.label), count: v.count })),
      ];
    }),
  );
